import { Edit, Trash2, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import type { ModeTableProps } from "@/types/api.types";
import { Switch } from "@/components/ui/switch";

export default function ModeTable({
  modes,
  isLoading,
  searchTerm,
  onSearchChange,
  onEdit,
  onView,
  onDelete,
  onToggle,
}: ModeTableProps) {
  const filteredModes = modes.filter(
    (mode) =>
      mode.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mode.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mode.slug.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Search Bar */}
      <div className="p-4 border-b">
        <Input
          placeholder="Search modes..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="max-w-sm"
        />
      </div>

      {/* Table */}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Slug</TableHead>
            <TableHead>Route</TableHead>
            <TableHead>Order</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredModes.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                No modes found
              </TableCell>
            </TableRow>
          ) : (
            filteredModes.map((mode) => (
              <TableRow key={mode.id}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {mode.logo && (
                      <img
                        src={mode.logo}
                        alt={mode.name}
                        className="w-10 h-10 rounded object-cover"
                      />
                    )}
                    <div>
                      <div className="font-medium">{mode.name}</div>
                      {mode.description && (
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {mode.description}
                        </div>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                    {mode.slug}
                  </code>
                </TableCell>
                <TableCell>
                  <code className="text-xs text-gray-600">{mode.route}</code>
                </TableCell>
                <TableCell>{mode.order}</TableCell>
                <TableCell>
                  <Badge
                    variant={mode.isActive ? "success" : "secondary"}
                    className={
                      mode.isActive
                        ? "bg-green-100 text-white"
                        : "bg-gray-100 text-gray-800"
                    }
                  >
                    {mode.isActive ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onView(mode)}
                      title="View"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Switch
                      checked={mode.isActive}
                      onCheckedChange={() => onToggle(mode.id)}
                      className="
                      mt-1
    data-[state=checked]:bg-green-600
    data-[state=unchecked]:bg-red-500
  "
                    />

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(mode)}
                      title="Edit"
                    >
                      <Edit className="h-4 w-4 text-blue-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(mode.id)}
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
